/*
 * Voyado pagination — IntersectionObserver sentinel.
 * --------------------------------------------------
 * Caller supplies a root, a sentinel selector and onLoadMore(). Fires
 * onLoadMore when the sentinel enters the viewport, guarding re-entrancy while a
 * load is in flight. The caller owns the cursor and removes the sentinel when
 * there are no more pages.
 *
 * onLoadMore MUST return a promise that settles once the page has landed (or has been
 * given up on) — both the busy latch and the level re-check below wait on it. Returning
 * nothing releases the latch on the next microtask, which is what let a fast fling fire
 * several duplicate requests for the same cursor.
 */
;(function () {
  'use strict';

  const ROOT_MARGIN = 200; // px — the observer and inView() must agree, so keep it in one place

  function createVoyadoPagination(opts) {
    const sentinel = opts.root.querySelector(opts.sentinelSelector);
    let busy = false;

    // Is the sentinel inside the observer's expanded root right now? Read the geometry
    // directly rather than trusting the last callback — see the re-check in run().
    function inView() {
      if (!sentinel || !sentinel.isConnected) return false;
      const r = sentinel.getBoundingClientRect();
      const h = window.innerHeight || document.documentElement.clientHeight;
      return r.bottom > -ROOT_MARGIN && r.top < h + ROOT_MARGIN;
    }

    function run() {
      if (busy || !sentinel || !sentinel.isConnected) return;
      busy = true;
      const heightBefore = document.documentElement.scrollHeight;
      Promise.resolve(opts.onLoadMore()).finally(function () {
        busy = false;
        // An observer reports only a CHANGE of intersection, so a page that lands while the
        // sentinel is still in view leaves nothing to re-arm the trigger and its spinner runs
        // on for ever. Re-test the geometry ourselves, gated on the document having actually
        // grown, so a response that appends nothing can never loop on the API.
        if (document.documentElement.scrollHeight > heightBefore && inView()) run();
      });
    }

    const observer = new IntersectionObserver(function (entries) {
      // Records arrive batched when the main thread was busy between an observation and its
      // delivery — routine during a fast fling, and right after a page of cards is appended.
      // The LAST record is the current state; entries[0] can be a stale exit or observe()'s
      // own initial not-intersecting record, and reading it dropped the load outright, with no
      // further crossing left to re-arm the trigger (the "spinner spins for ever" snag).
      const now = entries[entries.length - 1];
      if (now && now.isIntersecting) run();
    }, { rootMargin: ROOT_MARGIN + 'px' });

    if (sentinel) observer.observe(sentinel);

    return { destroy: function () { observer.disconnect(); } };
  }

  window.createVoyadoPagination = createVoyadoPagination;
})();
